// Rider-seitige Admin-Helpers.
//
// Warum im Rider-PWA und nicht nur in der Admin-SPA?
// User-Wunsch Batch 7: "wenn ich mit dem handy unterwegs bin, mit der app,
// sollte ich auch als admin im trail anhalten können und die aktuelle
// location als crux zum trail hinzufügen können. das wäre sehr praktikabel.
// entfernung soll dennoch im admin gehen, wenn man das möchte (gut am
// komputer), aber vor ort viel besser zu pflegen, ich halte an und gebe es
// ein, DA wo es gefährlich ist."
//
// User-Nachschub Batch 7 (nach dem ersten Feldtest):
// "crux gesetzt […] war da, drauf geklickt (auf das logo) verschwunden.
//  sollte nie verschwinden.
//  die gesetzten crux an den server schicken und speichern, sie sollten als
//  POIs gehandhabt werden und auch genauso gecached werden finde ich.
//  sie müssen unbedingt auch offline zugänglich sein!"
//
// Konsequenzen für das Design:
//   (a) Offline-First: der Crux wird IMMER sofort lokal in die trails-IDB-Row
//       geschrieben, bevor wir Netz probieren. Damit ist er für den Rider
//       direkt sichtbar und übersteht Reload / Flugmodus.
//   (b) Pending-Queue: falls PATCH fehlschlägt (offline, Token kaputt, 500
//       etc.), landet der komplette neue cruxPoints-Stand pro Trail in
//       cruxQueue (IDB-Store). flushCruxQueue() retry't beim nächsten Start
//       und bei jedem online-Event.
//   (c) "Last-write-wins"-Merge: das Backend überschreibt cruxPoints atomar,
//       d.h. wir schicken bei jedem Flush die LATEST volle Liste, nicht
//       Inkremente. Zwei-Admin-Race ist möglich, aber nicht im Scope
//       (normale Kontrollparty ist ein Admin vor Ort).

import type { CruxPoint } from '@adtu/shared';
import { getDb } from './storage/db';

interface PatchTrailResponse {
  id: string;
  cruxPoints?: CruxPoint[];
  updatedAt: string;
}

export interface AdminPatchTrailInput {
  cruxPoints?: CruxPoint[] | null;
}

/**
 * Schreibt die neue cruxPoints-Liste lokal in die trails-IDB-Row. Idempotent.
 * Wird sowohl beim optimistischen Apply als auch nach dem erfolgreichen PATCH
 * gerufen (letzteres überschreibt mit den serverseitig normalisierten Punkten).
 */
export async function applyCruxesLocally(
  trailId: string,
  cruxPoints: CruxPoint[],
): Promise<void> {
  try {
    const db = await getDb();
    const existing = await db.get('trails', trailId);
    if (existing) {
      await db.put('trails', {
        ...existing,
        cruxPoints,
        // cachedAt NICHT bumpen — wir haben die GPX nicht neu gezogen. Der
        // nächste syncTrailCache-Lauf wird per JSON-deep-Compare der cruxPoints
        // entscheiden, ob re-download nötig ist (ist er bei unveränderter Liste
        // nicht, passt also).
      });
    }
  } catch (err) {
    console.warn('[admin] applyCruxesLocally failed', err);
    throw err;
  }
}

async function enqueuePending(
  trailId: string,
  cruxPoints: CruxPoint[],
  errorMessage: string,
): Promise<void> {
  try {
    const db = await getDb();
    const existing = await db.get('cruxQueue', trailId);
    await db.put('cruxQueue', {
      trailId,
      cruxPoints,
      queuedAt: existing?.queuedAt ?? new Date().toISOString(),
      attempts: (existing?.attempts ?? 0) + 1,
      lastError: errorMessage,
    });
    emitQueueChanged();
  } catch (err) {
    console.warn('[admin] enqueuePending failed', err);
  }
}

async function dequeuePending(trailId: string): Promise<void> {
  try {
    const db = await getDb();
    await db.delete('cruxQueue', trailId);
    emitQueueChanged();
  } catch (err) {
    console.warn('[admin] dequeuePending failed', err);
  }
}

export async function listPendingCruxTrailIds(): Promise<string[]> {
  try {
    const db = await getDb();
    const keys = await db.getAllKeys('cruxQueue');
    return keys as string[];
  } catch {
    return [];
  }
}

export interface PendingCruxItem {
  trailId: string;
  count: number;          // Anzahl Cruxes in diesem pending-Snapshot
  queuedAt: string;
  attempts: number;
  lastError?: string;
}

/**
 * Liefert alle pending Crux-Queue-Items samt Metadaten. Die UI (ProfilePage)
 * nutzt das, um dem User sichtbar zu machen "da liegt noch was, das nicht
 * beim Server ist" — User-Wunsch: "die crus, die ich üer die location am
 * handy markiert habe, ging i-wie nicht bis zum backend" → transparente
 * Anzeige + manueller Sync-Button.
 */
export async function listPendingCruxItems(): Promise<PendingCruxItem[]> {
  try {
    const db = await getDb();
    const items = await db.getAll('cruxQueue');
    return items.map((i) => ({
      trailId: i.trailId,
      count: i.cruxPoints.length,
      queuedAt: i.queuedAt,
      attempts: i.attempts,
      lastError: i.lastError,
    }));
  } catch {
    return [];
  }
}

/** Custom-Event-Name: die Queue hat sich geändert (enqueue/dequeue/flush).
 *  Jede UI, die pending-Items anzeigt, kann darauf hören und neu lesen. */
export const CRUX_QUEUE_CHANGED_EVENT = 'adt:crux-queue-changed';

function emitQueueChanged() {
  try {
    window.dispatchEvent(new CustomEvent(CRUX_QUEUE_CHANGED_EVENT));
  } catch {
    // No-op bei SSR / Tests ohne window.
  }
}

/**
 * Low-level: PATCH /api/admin/trails/:id. Wirft nur bei echten Fehlern
 * (Netz weg, 4xx, 5xx) — damit der Aufrufer zwischen "synced" und "pending"
 * unterscheiden kann.
 *
 * Wave 14cf: zwei Auth-Pfade parallel:
 *   1) Bearer-Token (Master-Admin-Token, vom User in Profile gesetzt).
 *      Legacy-Pfad — funktioniert weiterhin wenn der User den Token kennt.
 *   2) Session-Cookie (`credentials: 'include'`). Wenn der User eine
 *      eingeloggte Session mit role=admin hat, akzeptiert `requireAdmin`
 *      im Backend (Wave 14bt-fix) das ebenfalls — kein Master-Token nötig.
 *      Das ist der dominante Pfad für PUSIO-Backdoor-Login und für jeden
 *      User der per Magic-Link als Admin eingeloggt ist.
 *
 * Vorher hat ein leerer Token sofort einen Wurf ausgelöst, sodass der
 * Session-Cookie-Pfad nie zum Server kam und alle Crux-Edits in der Queue
 * landeten obwohl die Auth perfekt da war. User-Bug 14cf: "ich sehe
 * Schlüsselstellen NICHT nach dem Ride".
 */
async function rawPatchTrail(
  trailId: string,
  token: string,
  input: AdminPatchTrailInput,
): Promise<PatchTrailResponse> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  const res = await fetch(`/api/admin/trails/${encodeURIComponent(trailId)}`, {
    method: 'PATCH',
    headers,
    body: JSON.stringify(input),
    // Session-Cookie mitschicken — backend requireAdmin akzeptiert beide
    // (Bearer-Token oder eingeloggte Admin-Session) seit Wave 14bt-fix.
    credentials: 'include',
  });

  if (res.status === 401 || res.status === 403) {
    throw new Error('admin_token_invalid');
  }
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`patchTrail ${res.status}: ${text}`);
  }
  return (await res.json()) as PatchTrailResponse;
}

export interface AppendCruxResult {
  /** Der aktuelle cruxPoints-Stand nach dem Apply. */
  cruxPoints: CruxPoint[];
  /**
   * true: erfolgreich zum Server gepusht.
   * false: offline oder Server-Fehler, in cruxQueue gepark — wird später retry'd.
   */
  synced: boolean;
  /** Bei synced=false: Kurzbeschreibung warum's nicht ging. */
  error?: string;
}

/**
 * Hauptfunktion: hängt eine neue Crux an die bestehenden dran, schreibt lokal,
 * versucht zu pushen. Wirft NIEMALS, damit die UI nicht knallen kann. Rückgabe-
 * Feld `synced` zeigt, ob der Stand schon beim Server ist.
 */
export async function appendCruxPoint(
  trailId: string,
  token: string,
  existing: CruxPoint[],
  newPoint: CruxPoint,
): Promise<AppendCruxResult> {
  // Höhe: nur durchreichen, wenn endlich — NaN / Infinity vom Geolocation-
  // API haben wir schon beim Aufruf filtern sollen, aber defensiv hier auch.
  const eleRaw = newPoint.elevationMeters;
  const elevationMeters =
    eleRaw != null && Number.isFinite(eleRaw)
      ? Math.round(eleRaw)
      : null;
  const rounded: CruxPoint = {
    distanceMeters: Math.max(0, Math.round(newPoint.distanceMeters)),
    note: newPoint.note?.trim() ? newPoint.note.trim().slice(0, 120) : null,
    elevationMeters,
  };
  // Merge-Schutz gegen Doppel-Tap (User-Wunsch: 5m Toleranz). Wenn der neue
  // Punkt zu nah an einem bestehenden liegt, ersetzen wir den alten statt
  // einen Cluster zu bauen.
  const MERGE_DIST = 5;
  const merged = existing.filter(
    (c) => Math.abs(c.distanceMeters - rounded.distanceMeters) > MERGE_DIST,
  );
  merged.push(rounded);
  merged.sort((a, b) => a.distanceMeters - b.distanceMeters);

  // (a) Optimistisch lokal speichern — User sieht den Punkt SOFORT, auch
  // offline. Bei IDB-Error werfen wir, weil dann selbst die lokale Persistenz
  // nicht klappt (z.B. Incognito/private mode with DB disabled) — in dem Fall
  // ist der Feature-Use-Case sowieso defekt.
  await applyCruxesLocally(trailId, merged);

  // (b) Netz-Push versuchen. Bei jedem Fehler (inkl. Offline) queuen.
  // Wave 14cf: kein vorzeitiger Abort bei leerem Token — rawPatchTrail
  // schickt jetzt zusätzlich credentials:'include', sodass die eingeloggte
  // Admin-Session genauso als Auth akzeptiert wird wie der Master-Token.
  // Bei iOS-PWA-Cookie-Eviction fällt der request auf 401 und landet
  // sauber in der Queue.
  try {
    const data = await rawPatchTrail(trailId, token, { cruxPoints: merged });
    const serverCruxes = Array.isArray(data.cruxPoints) ? data.cruxPoints : merged;
    // Server-Stand lokal übernehmen (normalisiert, garantiert sortiert).
    await applyCruxesLocally(trailId, serverCruxes);
    await dequeuePending(trailId);
    return { cruxPoints: serverCruxes, synced: true };
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    console.warn('[admin] appendCruxPoint: PATCH failed, queued for retry', msg);
    await enqueuePending(trailId, merged, msg);
    return { cruxPoints: merged, synced: false, error: msg };
  }
}

export interface FlushResult {
  tried: number;
  synced: number;
  stillPending: number;
}

/**
 * Drain der cruxQueue. Wird im App-Root aufgerufen (Mount + 'online'-Event).
 * Stumm wenn die Queue leer ist. Bei Auth-Fehlern bricht der Lauf hart ab —
 * wir retry'n nicht 10 Mal gegen einen kaputten Token.
 */
export async function flushCruxQueue(token: string): Promise<FlushResult> {
  if (!token) return { tried: 0, synced: 0, stillPending: 0 };
  let tried = 0;
  let synced = 0;
  let stillPending = 0;

  let items: Array<{ trailId: string; cruxPoints: CruxPoint[] }> = [];
  try {
    const db = await getDb();
    const all = await db.getAll('cruxQueue');
    items = all.map((i) => ({ trailId: i.trailId, cruxPoints: i.cruxPoints }));
  } catch (err) {
    console.warn('[admin] flushCruxQueue: couldn\'t read queue', err);
    return { tried: 0, synced: 0, stillPending: 0 };
  }
  if (items.length === 0) return { tried: 0, synced: 0, stillPending: 0 };

  for (const item of items) {
    tried += 1;
    try {
      const data = await rawPatchTrail(item.trailId, token, {
        cruxPoints: item.cruxPoints,
      });
      const serverCruxes = Array.isArray(data.cruxPoints)
        ? data.cruxPoints
        : item.cruxPoints;
      await applyCruxesLocally(item.trailId, serverCruxes);
      await dequeuePending(item.trailId);
      synced += 1;
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      await enqueuePending(item.trailId, item.cruxPoints, msg);
      stillPending += 1;
      // Token kaputt → restliche Items skippen, sonst 20x denselben Fehler.
      if (msg.includes('admin_token_invalid') || msg.includes('admin_token_missing')) {
        stillPending += items.length - tried;
        break;
      }
    }
  }

  if (synced > 0) {
    console.info(`[admin] flushCruxQueue: ${synced}/${tried} synced, ${stillPending} pending`);
  }
  return { tried, synced, stillPending };
}

// ─────────────────────────────────────────────────────────────────────────────
// Wave 14ca-fix39: in-app Trail-Edit aus dem Rider heraus.
// User-Wunsch: Admin will einen Trail direkt im Rider editieren (Name, Type,
// Difficulty, Color, Reverse), ohne in die separate Admin-Web-App wechseln zu
// muessen. Diese Funktionen spiegeln updateTrail / reverseTrail aus
// apps/admin/src/api.ts, mit dem gleichen Bearer-Token-Pattern wie
// appendCruxPoint oben — der lokale `useAdminToken()`-Wert wird mitgeschickt.
// ─────────────────────────────────────────────────────────────────────────────

import type { Difficulty, TrailType } from '@adtu/shared';

export interface UpdateTrailInput {
  name?: string;
  trailType?: TrailType;
  difficulty?: Difficulty;
  /** Hex-Wert wie '#ff00aa', null = explizit auf Difficulty-Default zuruecksetzen,
      omit = unveraendert. */
  colorHex?: string | null;
  description?: string | null;
}

export interface UpdateTrailResult {
  ok: boolean;
  /** Frischer Trail-Stand vom Server (id/name/colorHex/etc) — fuer optimistic
      UI-Update und Cache-Refresh. */
  trail?: {
    id: string;
    name: string;
    trailType: TrailType;
    difficulty: Difficulty;
    colorHex?: string | null;
    description?: string | null;
    updatedAt: string;
  };
  error?: string;
}

export async function updateTrailMeta(
  trailId: string,
  token: string,
  input: UpdateTrailInput,
): Promise<UpdateTrailResult> {
  // Wave 14cf: kein Hard-Abort mehr bei leerem Token — Session-Cookie
  // (credentials:'include') ist ein gleichwertiger Auth-Pfad.
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  try {
    const res = await fetch(
      `/api/admin/trails/${encodeURIComponent(trailId)}`,
      {
        method: 'PATCH',
        headers,
        body: JSON.stringify(input),
        credentials: 'include',
      },
    );
    if (res.status === 401 || res.status === 403) {
      return { ok: false, error: 'admin_token_invalid' };
    }
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      return { ok: false, error: `http_${res.status}: ${text}` };
    }
    const trail = (await res.json()) as UpdateTrailResult['trail'];
    return { ok: true, trail };
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : 'network' };
  }
}

export interface ReverseTrailResult {
  ok: boolean;
  trail?: {
    id: string;
    updatedAt: string;
  };
  error?: string;
}

export async function reverseTrailDirection(
  trailId: string,
  token: string,
): Promise<ReverseTrailResult> {
  // Wave 14cf: Session-Cookie als Auth-Pfad neben Bearer-Token.
  const headers: Record<string, string> = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  try {
    const res = await fetch(
      `/api/admin/trails/${encodeURIComponent(trailId)}/reverse`,
      {
        method: 'POST',
        headers,
        credentials: 'include',
      },
    );
    if (res.status === 401 || res.status === 403) {
      return { ok: false, error: 'admin_token_invalid' };
    }
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      return { ok: false, error: `http_${res.status}: ${text}` };
    }
    const trail = (await res.json()) as ReverseTrailResult['trail'];
    return { ok: true, trail };
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : 'network' };
  }
}
